import type { RouteLocationNormalizedLoaded } from 'vue-router';

const languages: any = {
    en: ['uk', 'en', 'us', 'au', 'ca', 'ie', 'nz', 'za', 'hu', 'gr', 'ru'],
    fr: ['fr', 'be', 'ca-fr', 'ch-fr'],
    es: ['es', 'cl', 'mx', 'ar'],
    it: ['it'],
    de: ['de', 'at', 'ch'],
    nl: ['nl', 'be-nl'],
    pl: ['pl'],
    pt: ['pt', 'br'],
};

export const paths: any = {
    parking: {
        en: 'airport-parking',
        es: 'aparcamiento-en-el-aeropuerto',
        fr: 'parking-a-aeroport',
        de: 'flughafen-parkplatze',
        pt: 'estacionamento-do-aeroporto',
        it: 'parcheggio-aeroporto',
        nl: 'parkeren-bij-de-luchthaven',
        pl: 'parking-lotnisko',
        jp: 'airport-parking',
    },
    'port-parking': {
        en: 'port-parking',
        es: 'estacionamiento-del-puerto',
        fr: 'parking-du-port',
        de: '',
        pt: '',
        it: 'parcheggio-porto',
        nl: 'poort-parkeren',
        pl: '',
        jp: '',
    },
    transfers: {
        en: 'airport-transfers',
        es: 'traslado-aeroporto',
        fr: 'transferts-aeroports',
        de: 'flughafen-transfer',
        pt: 'transfers-para-o-aeroporto',
        it: 'transfer-aeroportuali',
        nl: 'luchthaventransfers',
        pl: 'lotniskowe-transfery',
        jp: 'transfers',
    },
    'hotel-with-parking': {
        en: 'airport-hotels-and-parking',
        es: '',
        fr: '',
        de: '',
        pt: '',
        it: '',
        nl: '',
        pl: '',
        jp: '',
    },
    'train-station-parking': {
        en: 'train-station-parking',
        es: 'aparcamiento-en-la-estacion-de-tren',
        fr: '',
        de: '',
        pt: 'trainstation-parking',
        it: 'parcheggio-stazione-treni',
        nl: '',
        pl: '',
        jp: '',
    },
};

export function getLanguageFromRoute(
    route: RouteLocationNormalizedLoaded,
    locale: string,
    locales: { code: string; storyblok?: string }[]
) {
    const routePath = route.path?.startsWith('/') ? route.path.slice(1) : route.path;
    const location = routePath.split('/')[0];

    return location.length > 2 ? location.split('-')[1] : locales.filter((e) => e.code === locale)[0].storyblok;
}

export function getLanguageForAPI(lang: string) {
    if (lang) {
        let language = '';
        switch (lang) {
            case 'uk':
                language = 'uk';
                break;
            case 'ca-fr':
                language = 'ca';
                break;
            case 'ch-fr':
                language = 'ca';
                break;
            case 'be-nl':
                language = 'be';
                break;
            default:
                language = lang;
        }
        return language;
    }
}

export function getLanguageForLocale(lang: string | string[] | undefined) {
    if (lang) {
        let language = '';

        (Object.keys(languages) as (keyof typeof languages)[]).forEach((key: any) => {
            if (languages[key].includes(lang)) {
                language = key;
            }
        });

        return language;
    }
}

export function getLanguageForStoryblok(lang: string) {
    if (lang) {
        let language = '';
        switch (lang) {
            case 'uk':
                language = 'en';
                break;
            case 'ca-fr':
                language = 'fr';
                break;
            case 'ch-fr':
                language = 'fr';
                break;
            case 'be-nl':
                language = 'nl';
                break;
            default:
                language = lang;
        }
        return language;
    }
}

export function getProductTypeSlug(lang: string | undefined, type: string) {
    const typeI18n: { [key: string]: { [key: string]: string } } = {
        en: {
            'airport-parking': 'airport-parking',
            'port-parking': 'port-parking',
            'airport-transfers': 'airport-transfers',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        de: {
            'airport-parking': 'flughafen-parkplatze',
            'port-parking': 'port-parking',
            'airport-transfers': 'flughafen-transfer',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        pl: {
            'airport-parking': 'parking-lotnisko',
            'port-parking': 'port-parking',
            'airport-transfers': 'lotniskowe-transfery',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        fr: {
            'airport-parking': 'parking-a-aeroport',
            'port-parking': 'port-parking',
            'airport-transfers': 'transferts-aeroports',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        it: {
            'airport-parking': 'parcheggio-aeroporto',
            'port-parking': 'parcheggio-porto',
            'airport-transfers': 'transfer-aeroportuali',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        pt: {
            'airport-parking': 'estacionamento-do-aeroporto',
            'port-parking': 'estacionamiento-del-puerto',
            'airport-transfers': 'transfers-para-o-aeroporto',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        es: {
            'airport-parking': 'aparcamiento-en-el-aeropuerto',
            'port-parking': 'estacionamiento-del-puerto',
            'airport-transfers': 'traslado-aeroporto',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
        nl: {
            'airport-parking': 'parkeren-bij-de-luchthaven',
            'port-parking': 'port-parking',
            'airport-transfers': 'airport-transfers',
            'airport-hotels-and-parking': 'airport-hotels-and-parking',
            'all-parking': 'all-parking',
        },
    };

    let page = '';
    (Object.keys(languages) as (keyof typeof languages)[]).forEach((key) => {
        if (languages[key].includes(lang)) {
            page = typeI18n[key as any][type];
        }
    });

    return page;
}

/**
 * Search URL slug translation for SearchBox
 *
 * @remarks
 * I18n setup to use lazyloading, so only active locale messages awailable in components.
 * This function allow to get correct URL for the desired country regardless of the active locale
 *
 * @param countryCode - A country code, it will be used to get correct locale
 * @param type - type of locations to search
 *
 * @returns search box URL slug for provided country
 */
export function getLocationSearchSlug(
    country: string,
    type: 'parking' | 'port' | 'port-parking' | 'hotel' | 'transfers' | 'train-station-parking'
) {
    const typeI18n: { [key: string]: { [key: string]: string } } = {
        en: {
            parking: 'airport-parking',
            port: 'port-parking',
            'port-parking': 'port-parking',
            transfers: 'airport-transfers',
            'search-results': 'search-results',
            hotel: 'hotel',
        },
        de: {
            parking: 'flughafen-parkplatze',
            port: 'port-parking',
            transfers: 'flughafen-transfer',
            'search-results': 'suchergebnisse',
            hotel: 'hotel',
        },
        pl: {
            parking: 'parking-lotnisko',
            port: 'port-parking',
            transfers: 'lotniskowe-transfery',
            'search-results': 'wyniki-wyszukiwania',
            hotel: 'hotel',
        },
        fr: {
            parking: 'parking-a-aeroport',
            port: 'parking-du-port',
            transfers: 'transferts-aeroports',
            'search-results': 'resultats-de-recherche',
            hotel: 'hotel',
        },
        it: {
            parking: 'parcheggio-aeroporto',
            port: 'parcheggio-porto',
            transfers: 'transfer-aeroportuali',
            'search-results': 'risultati-della-ricerca',
            'train-station-parking': 'parcheggio-stazione-treni',
            'port-parking': 'parcheggio-porto',
            hotel: 'hotel',
        },
        pt: {
            parking: 'estacionamento-do-aeroporto',
            port: 'port-parking',
            transfers: 'transfers-para-o-aeroporto',
            'search-results': 'resultados-da-pesquisa',
            'train-station-parking': 'trainstation-parking',
            hotel: 'hotel',
        },
        es: {
            parking: 'aparcamiento-en-el-aeropuerto',
            port: 'estacionamiento-del-puerto',
            'port-parking': 'estacionamiento-del-puerto',
            transfers: 'traslado-aeroporto',
            'search-results': 'resultados-de-busqueda',
            'train-station-parking': 'aparcamiento-en-la-estacion-de-tren',
            hotel: 'hotel',
        },
        nl: {
            parking: 'parkeren-bij-de-luchthaven',
            port: 'port-parking',
            transfers: 'transfers',
            'search-results': 'zoekresultaten',
            hotel: 'hotel',
        },
    };

    const countryCode = country?.toLocaleLowerCase() || 'uk';

    let url = '';
    (Object.keys(languages) as (keyof typeof languages)[]).forEach((key) => {
        if (languages[key].includes(countryCode) && countryCode !== 'gr' && countryCode !== 'hu') {
            url = `${countryCode}/${typeI18n[key as any][type]}/${typeI18n[key as any]['search-results']}`;
        }
    });
    if (!url) {
        url = `uk/${typeI18n['en'][type]}/${typeI18n['en']['search-results']}`;
    }

    return url;
}

export const locales = [
    {
        code: 'us',
        storyblok: 'en',
        iso: 'en-US',
        file: 'en.js',
        lang: 'English',
        name: 'United States',
    },
    {
        code: 'uk',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'United Kingdom',
    },
    {
        code: 'au',
        storyblok: 'en',
        iso: 'en-AU',
        file: 'en.js',
        lang: 'English',
        name: 'Australia',
    },
    {
        code: 'ca',
        storyblok: 'en',
        iso: 'en-CA',
        file: 'en.js',
        lang: 'English',
        name: 'Canada',
    },
    {
        code: 'ie',
        storyblok: 'en',
        iso: 'en-IE',
        file: 'en.js',
        lang: 'English',
        name: 'Ireland',
    },
    {
        code: 'nz',
        storyblok: 'en',
        iso: 'en-NZ',
        file: 'en.js',
        lang: 'English',
        name: 'New Zealand',
    },
    {
        code: 'za',
        storyblok: 'en',
        iso: 'en-ZA',
        file: 'en.js',
        lang: 'English',
        name: 'South Africa',
    },
    {
        code: 'es',
        storyblok: 'es',
        iso: 'es-ES',
        file: 'es.js',
        lang: 'Spanish',
        name: 'España',
    },
    {
        code: 'cl',
        storyblok: 'es',
        iso: 'es-CL',
        file: 'es.js',
        lang: 'Spanish',
        name: 'Chile',
    },
    {
        code: 'ar',
        storyblok: 'es',
        iso: 'es-AR',
        file: 'es.js',
        lang: 'Spanish',
        name: 'Argentina',
    },
    {
        code: 'mx',
        storyblok: 'es',
        iso: 'es-MX',
        file: 'es.js',
        lang: 'Spanish',
        name: 'México',
    },
    {
        code: 'fr',
        storyblok: 'fr',
        iso: 'fr-FR',
        file: 'fr.js',
        lang: 'French',
        name: 'France',
    },
    {
        code: 'fr',
        storyblok: 'fr',
        iso: 'fr-CH',
        file: 'fr.js',
        lang: 'French',
        name: 'Suisse',
    },
    {
        code: 'ca-fr',
        storyblok: 'fr',
        iso: 'fr-CA',
        file: 'fr.js',
        lang: 'French',
        name: 'Canada',
    },
    {
        code: 'be',
        storyblok: 'fr',
        iso: 'fr-BE',
        file: 'fr.js',
        lang: 'French',
        name: 'Belgique',
    },
    {
        code: 'de',
        storyblok: 'de',
        iso: 'de-DE',
        file: 'de.js',
        lang: 'German',
        name: 'Deutschland',
    },
    {
        code: 'ch',
        storyblok: 'de',
        iso: 'de-CH',
        file: 'de.js',
        lang: 'German',
        name: 'Schweiz',
    },
    {
        code: 'ch-fr',
        storyblok: 'fr',
        iso: 'fr-CH',
        file: 'fr.js',
        lang: 'French',
        name: 'Suisse',
    },
    {
        code: 'at',
        storyblok: 'de',
        iso: 'de-AT',
        file: 'de.js',
        lang: 'German',
        name: 'Österreich',
    },
    {
        code: 'pt',
        storyblok: 'pt',
        iso: 'pt-PT',
        file: 'pt.js',
        lang: 'Portuguese',
        name: 'Portugal',
    },
    {
        code: 'br',
        storyblok: 'pt',
        iso: 'pt-BR',
        file: 'pt.js',
        lang: 'Portuguese',
        name: 'Brasil',
    },
    {
        code: 'it',
        storyblok: 'it',
        iso: 'it-IT',
        file: 'it.js',
        lang: 'Italian',
        name: 'Italia',
    },
    {
        code: 'nl',
        storyblok: 'nl',
        iso: 'nl-NL',
        file: 'nl.js',
        lang: 'Dutch',
        name: 'Nederland',
    },
    {
        code: 'be-nl',
        storyblok: 'nl',
        iso: 'nl-BE',
        file: 'nl.js',
        lang: 'Dutch',
        name: 'België',
    },
    {
        code: 'pl',
        storyblok: 'pl',
        iso: 'pl-PL',
        file: 'pl.js',
        lang: 'Polish',
        name: 'Polska',
    },
    {
        code: 'hu',
        storyblok: 'de',
        iso: 'de-HU',
        file: 'de.js',
        lang: 'German',
        name: 'Hungary',
    },
    {
        code: 'cy',
        storyblok: 'es',
        iso: 'es-CY',
        file: 'es.js',
        lang: 'Spanish',
        name: 'Cyprus',
    },
    {
        code: 'gr',
        storyblok: 'es',
        iso: 'es-GR',
        file: 'es.js',
        lang: 'Spanish',
        name: 'Greece',
    },
    {
        code: 'jp',
        storyblok: 'jp',
        iso: 'jp-JP',
        file: 'jp.js',
        lang: 'Japanese',
        name: '日本',
    },
    {
        code: 'hr',
        // storyblok: 'hr', // original values commented to use en locale for now
        // iso: 'hr-HR',
        // file: 'hr.js',
        // lang: 'Croatian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Hrvatska',
    },
    {
        code: 'bg',
        // storyblok: 'bg',
        // iso: 'bg-BG',
        // file: 'bg.js',
        // lang: 'Bulgarian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'България',
    },
    {
        code: 'cz',
        // storyblok: 'cs',
        // iso: 'cs-CZ',
        // file: 'cz.js',
        // lang: 'Czech',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Česká republika',
    },
    {
        code: 'dk',
        // storyblok: 'da',
        // iso: 'da-DK',
        // file: 'dk.js',
        // lang: 'Danish',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Denmark',
    },
    {
        code: 'ee',
        // storyblok: 'et',
        // iso: 'et-EE',
        // file: 'ee.js',
        // lang: 'Estonian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Eesti',
    },
    {
        code: 'fi',
        // storyblok: 'fi',
        // iso: 'fi-FI',
        // file: 'fi.js',
        // lang: 'Finnish',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Suomi',
    },
    {
        code: 'lv',
        // storyblok: 'lv',
        // iso: 'lv-LV',
        // file: 'lv.js',
        // lang: 'Latvian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Latvija',
    },
    {
        code: 'lt',
        // storyblok: 'lt',
        // iso: 'lt-LT',
        // file: 'lt.js',
        // lang: 'Lithuanian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Lietuva',
    },
    {
        code: 'sk',
        // storyblok: 'sk',
        // iso: 'sk-SK',
        // file: 'sk.js',
        // lang: 'Slovak',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Slovensko',
    },
    {
        code: 'si',
        // storyblok: 'sl',
        // iso: 'sl-SI',
        // file: 'si.js',
        // lang: 'Slovenian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Slovenija',
    },
    {
        code: 'se',
        // storyblok: 'sv',
        // iso: 'sv-SE',
        // file: 'se.js',
        // lang: 'Swedish',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Sverige',
    },
    {
        code: 'al',
        // storyblok: 'sq',
        // iso: 'sq-AL',
        // file: 'al.js',
        // lang: 'Albanian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Shqipëria',
    },
    {
        code: 'ba',
        // storyblok: 'bs',
        // iso: 'bs-BA',
        // file: 'ba.js',
        // lang: 'Bosnian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Bosna',
    },
    {
        code: 'is',
        // storyblok: 'is',
        // iso: 'is-IS',
        // file: 'is.js',
        // lang: 'Icelandic',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Ísland',
    },
    {
        code: 'lu',
        // storyblok: 'fr',
        // iso: 'fr-LU',
        // file: 'fr.js',
        // lang: 'French',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Luxembourg',
    },
    {
        code: 'rs',
        // storyblok: 'sr',
        // iso: 'sr-SP',
        // file: 'rs.js',
        // lang: 'Serbian',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Srbija',
    },
    {
        code: 'tr',
        // storyblok: 'tr',
        // iso: 'tr-TR',
        // file: 'tr.js',
        // lang: 'Turkish',
        storyblok: 'en',
        iso: 'en-GB',
        file: 'en.js',
        lang: 'English',
        name: 'Türkiye',
    },
];
